import type { Dispatch, SetStateAction } from 'react';
import type { PageType, TableHeader, VisibleCategories } from '@/types';
import { TotalImpressionsTooltip } from '@/components/tooltips/ImpressionsTooltips';
import Checkbox from '@/components/Checkbox';
import {
  areAllCategoriesVisible,
  areAnyCategoriesVisible,
  toggleAllCategories,
} from './CheckboxUtils';

interface TableColumnsProps {
  popoverRef: React.RefObject<HTMLDivElement>;
  visibleCategories: VisibleCategories;
  setVisibleCategories: Dispatch<SetStateAction<VisibleCategories>>;
  pageType: PageType;
}

/**
 * This is the header data for the campaign types table.
 * @param popoverRef - The ref for the impressions tooltip.
 * @param visibleCategories - The visible campaign categories.
 * @param setVisibleCategories - The function to set the visible campaign categories.
 * @param pageType - The type of page. 'earnings' or 'payouts'
 * @returns The header data.
 */
const TableColumns = ({
  popoverRef,
  visibleCategories,
  setVisibleCategories,
  pageType,
}: TableColumnsProps): TableHeader[] => {
  const columns: TableHeader[] = [
    {
      id: 'campaignType',
      label: 'Campaign type',
      sortable: true,
      className:
        pageType === 'payouts'
          ? 'min-w-[80dvw] md:min-w-[500px] pl-6'
          : 'min-w-[80dvw] md:min-w-[500px]',
    },
    {
      id: 'impressions',
      label: <TotalImpressionsTooltip popoverRef={popoverRef} />,
      sortable: true,
      styleOptions: {
        align: 'right',
      },
    },
    {
      id: 'earnings',
      label: 'Estimated earnings',
      sortable: true,
      className: 'min-w-[200px]',
      styleOptions: {
        align: 'right',
      },
    },
    {
      id: 'percentage',
      label: '% of Total',
      sortable: true,
      className: 'min-w-[100px] pr-6',
      styleOptions: {
        align: 'right',
      },
    },
  ];

  if (pageType !== 'payouts') {
    columns.unshift({
      id: 'checkbox',
      label: (
        <Checkbox
          name="select-all-checkbox"
          className="translate-y-[2px]"
          checked={areAllCategoriesVisible(visibleCategories)}
          indeterminate={
            areAnyCategoriesVisible(visibleCategories) &&
            !areAllCategoriesVisible(visibleCategories)
          }
          onChange={() => {
            const shouldShowAll = !areAllCategoriesVisible(visibleCategories);
            setVisibleCategories(toggleAllCategories(visibleCategories, shouldShowAll));
          }}
        />
      ),
      sortable: false,
      className: 'w-[30px] pl-6 pr-3',
    });
  }

  return columns;
};

export default TableColumns;
